import { appStorage, appSession } from '../lib/safeStorage';
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

/**
 * Role-Preview Context — admin-only "view as staff/vendor" toggle.
 *
 * Lets admins safely preview the staff or vendor experience without logging
 * out. Persisted in appSession (not appStorage) so it never leaks past
 * the current tab session. Real `user` is preserved for /auth/me and any
 * server-side role checks; the override only affects client-side routing
 * and chrome (sidebar, RouteGuard, getHomeRoute).
 *
 * The override is silently ignored when the underlying user isn't already an
 * admin — staff or vendor users can never escalate themselves into admin
 * via this mechanism.
 */

const STORAGE_KEY = 'rolePreview';
// WHICH crew member is being previewed. A role is not a person: previewing
// the staff ROLE showed the admin's own (empty) staff view, so the app looked
// broken. Same appSession lifetime as the role above - never appStorage.
const STAFF_ID_KEY = 'rolePreviewStaffId';
const STAFF_NAME_KEY = 'rolePreviewStaffName';

export const RolePreviewContext = createContext({
  previewRole: null,
  setPreviewRole: () => {},
  previewStaffId: null,
  previewStaffName: '',
  setPreviewStaff: () => {},
  isPreviewing: false,
  effectiveUser: null,
});

const isAdminLike = (user) => {
  if (!user) return false;
  if (user.role === 'staff' || user.industryType === 'staff' || user.isStaff) return false;
  if (user.role === 'vendor' || user.industryType === 'vendor' || user.isVendor) return false;
  return true;
};

const buildEffectiveUser = (user, previewRole) => {
  if (!user || !previewRole || !isAdminLike(user)) return user;
  // Shallow override of role-deciding fields. The real user is kept for
  // identity/profile; only the role-routing flags are swapped.
  if (previewRole === 'staff') {
    return { ...user, role: 'staff', industryType: 'staff', isStaff: true, isVendor: false, _previewRole: 'staff' };
  }
  if (previewRole === 'vendor') {
    return { ...user, role: 'vendor', industryType: 'vendor', isVendor: true, isStaff: false, _previewRole: 'vendor' };
  }
  return user;
};

export function RolePreviewProvider({ user, children }) {
  const [previewRole, setPreviewRoleRaw] = useState(() => {
    try { return appSession.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  // Auto-clear preview when the underlying user is no longer admin-like
  // (e.g. logout + log in as staff). Prevents the banner sticking around in
  // a session where it doesn't apply.
  useEffect(() => {
    if (previewRole && !isAdminLike(user)) {
      try { appSession.removeItem(STORAGE_KEY); } catch {}
      setPreviewRoleRaw(null);
    }
  }, [user, previewRole]);

  const [previewStaffId, setPreviewStaffIdRaw] = useState(() => {
    try { return appSession.getItem(STAFF_ID_KEY) || null; } catch { return null; }
  });
  const [previewStaffName, setPreviewStaffNameRaw] = useState(() => {
    try { return appSession.getItem(STAFF_NAME_KEY) || ''; } catch { return ''; }
  });
  const setPreviewStaff = useCallback((id, name) => {
    try {
      if (id) {
        appSession.setItem(STAFF_ID_KEY, String(id));
        appSession.setItem(STAFF_NAME_KEY, String(name || ''));
      } else {
        appSession.removeItem(STAFF_ID_KEY);
        appSession.removeItem(STAFF_NAME_KEY);
      }
    } catch {}
    setPreviewStaffIdRaw(id ? String(id) : null);
    setPreviewStaffNameRaw(id ? String(name || '') : '');
  }, []);
  const setPreviewRole = useCallback((role) => {
    try {
      if (role) appSession.setItem(STORAGE_KEY, role);
      else appSession.removeItem(STORAGE_KEY);
    } catch {}
    // Leaving preview must drop the person too, or the next preview silently
    // inherits whoever was picked last.
    if (!role) {
      try {
        appSession.removeItem(STAFF_ID_KEY);
        appSession.removeItem(STAFF_NAME_KEY);
      } catch {}
      setPreviewStaffIdRaw(null);
      setPreviewStaffNameRaw('');
    }
    setPreviewRoleRaw(role);
  }, []);

  const value = useMemo(() => ({
    previewRole,
    setPreviewRole,
    previewStaffId,
    previewStaffName,
    setPreviewStaff,
    isPreviewing: !!previewRole && isAdminLike(user),
    effectiveUser: buildEffectiveUser(user, previewRole),
  }), [previewRole, setPreviewRole, previewStaffId, previewStaffName, setPreviewStaff, user]);

  return <RolePreviewContext.Provider value={value}>{children}</RolePreviewContext.Provider>;
}

export function useRolePreview() {
  return useContext(RolePreviewContext);
}
